import { createApi } from "@reduxjs/toolkit/query/react";
import { RIDER, TagId, TagType } from "../../configs";
import {
  Rider,
  PaginatedResponse,
  SearchByName,
  RiderDetails,
  IWriteRider,
  HttpMethod,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

const riderApi = createApi({
  reducerPath: RIDER.reducerPath,
  baseQuery: createBaseQuery(RIDER.baseUrl),
  tagTypes: RIDER.tagTypes,
  endpoints(builder) {
    return {
      fetchAllRiders: builder.query<Rider[], string | void>({
        query: (name) => {
          return {
            url: "/all",
            method: HttpMethod.GET,
            params: { name },
          };
        },
        providesTags: () => [{ type: TagType.Rider, id: TagId.ALL_LIST }],
      }),

      fetchRiders: builder.query<PaginatedResponse<Rider>, SearchByName>({
        query: (searchQuery) => {
          return {
            url: "/",
            method: HttpMethod.GET,
            params: searchQuery,
          };
        },
        providesTags: () => [{ type: TagType.Rider, id: TagId.LIST }],
      }),

      fetchRiderDetails: builder.query<RiderDetails, string>({
        query: (riderId) => {
          return {
            url: `/${riderId}`,
            method: HttpMethod.GET,
          };
        },
        providesTags: (_result, _error, riderId) => [
          { type: TagType.Rider, id: riderId },
        ],
      }),

      createRider: builder.mutation<RiderDetails, IWriteRider>({
        query: (body) => ({
          url: "",
          method: HttpMethod.POST,
          body,
        }),
        invalidatesTags: () => [
          { type: TagType.Rider, id: TagId.ALL_LIST },
          { type: TagType.Rider, id: TagId.LIST },
        ],
      }),

      updateRider: builder.mutation<
        void,
        { riderId: string; body: IWriteRider }
      >({
        query: ({ riderId, body }) => ({
          url: `/${riderId}`,
          method: HttpMethod.PUT,
          body,
        }),
        invalidatesTags: (_result, _error, { riderId }) => [
          { type: TagType.Rider, id: TagId.ALL_LIST },
          { type: TagType.Rider, id: TagId.LIST },
          { type: TagType.Rider, id: riderId },
        ],
      }),

      deleteRider: builder.mutation<void, string>({
        query: (riderId) => ({
          url: `/${riderId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, riderId) => [
          { type: TagType.Rider, id: TagId.ALL_LIST },
          { type: TagType.Rider, id: TagId.LIST },
          { type: TagType.Rider, id: riderId },
        ],
      }),
    };
  },
});

export const {
  useFetchAllRidersQuery,
  useFetchRidersQuery,
  useFetchRiderDetailsQuery,
  useCreateRiderMutation,
  useUpdateRiderMutation,
  useDeleteRiderMutation,
} = riderApi;

export { riderApi };
